import { useState, useCallback, useEffect, useRef } from 'react';
import * as serviceWorkerRegistration from 'serviceWorkerRegistration';
import locales from 'locales';
import { H1, Div } from 'components/common/CommonStyled';
import MuiButton from 'components/common/MuiButton';
import MuiModal from 'components/common/MuiModal';
// import { CircularProgress } from '@mui/material';
import storage from 'utils/storage';
import { Toast } from 'components/common/Toast';
import { useSelector } from 'react-redux';

function VersionUpdatePopup({ children }) {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [showReload, setShowReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const visibleUpdateModal = useSelector(
    state => state.general.visibleUpdateModal,
  );

  const version = process.env.REACT_APP_VERSION;
  const lastVersion = storage.get(`version`);
  const isNewVersion = lastVersion !== version;
  const isManualUpdate = useRef(false);

  // Check if service worker is activated
  const activated =
    navigator && 'serviceWorker' in navigator && navigator.serviceWorker
      ? navigator?.serviceWorker?.controller
        ? navigator.serviceWorker.controller.state === 'activated' ||
          navigator.serviceWorker.controller.state === 'activating'
        : !storage.get('manualUpdate') || storage.get('manualUpdate') !== 'on'
      : true;

  // Update version in local storage
  const updateVersion = () => storage.set({ version });

  // Skip waiting phase for service worker
  const skipWaiting = registrationWaiting => {
    if (registrationWaiting)
      registrationWaiting.postMessage({ type: 'SKIP_WAITING' });
  };

  // Open update modal
  const handleOpen = () => {
    setOpen(true);
  };

  // Close update modal
  const handleClose = () => {
    setOpen(false);
    window.location.reload(true);
  };

  // Callback function for service worker update
  const onSWUpdate = useCallback(registration => {
    let isMinorVersion;
    caches.keys().then(keys => {
      if (keys.length) {
        keys.forEach(cacheKey => {
          const cacheKeyVersion = cacheKey.split('pioAppVersion-')[1];
          if (version && cacheKeyVersion && !isMinorVersion)
            isMinorVersion =
              version.split('.')[0] !== cacheKeyVersion.split('.')[0] ||
              version.split('.')[1] !== cacheKeyVersion.split('.')[1];
        });
        if (isMinorVersion) {
          setShowReload(true);
          setWaitingWorker(registration.waiting);
        } else skipWaiting(registration.waiting);
      } else skipWaiting(registration.waiting);
    });
  }, []);

  // Reload page after updating service worker
  // simply put, this tells the service
  // worker to skip the waiting phase and then reloads the page
  const reloadPage = useCallback(() => {
    skipWaiting(waitingWorker);
    setShowReload(false);
  }, [waitingWorker]);

  // Handle confirmation of update
  const handleConfirm = async () => {
    setLoading(true);
    if (isManualUpdate.current) {
      storage.set({ manualUpdate: 'on' });
      await serviceWorkerRegistration.manualUpdate({
        callback: msg => {
          storage.set({ manualUpdate: 'off' });

          Toast(
            'warning',
            msg === 'no_service_worker'
              ? locales.noNewServiceWorker
              : locales.anErrorHasOccurred,
          );
          setLoading(false);
        },
      });
    } else reloadPage();
  };

  // Effect hook to handle update modal display
  useEffect(() => {
    if (showReload && waitingWorker && !isManualUpdate.current) handleOpen();
  }, [waitingWorker, showReload, reloadPage]);

  // Effect hook to handle version update
  useEffect(() => {
    if (activated && storage.get('manualUpdate') === 'on') {
      storage.set({ manualUpdate: 'off' });
      updateVersion();
    }

    if (activated && isNewVersion) updateVersion();
  }, [activated]);

  // Effect hook to handle manual update modal display
  useEffect(() => {
    if (visibleUpdateModal) {
      isManualUpdate.current = true;
      handleOpen();
    }
  }, [visibleUpdateModal]);

  // Effect hook to register service worker
  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: onSWUpdate,
    });
  }, []);

  // console.info(`sw activated: ${activated}`);

  return (
    <>
      {children}
      {/* {activated ? (
        children // Display children if service worker is activated
      ) : (
        // Display loading indicator if service worker is not activated
        <Div
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Div textAlign="center">
            <CircularProgress color="inherit" />
            <H1 fontSize={4} mb={4} mt={4}>
              {locales.update}
            </H1>
            <Div>{locales.updateContinues}</Div>
          </Div>
        </Div>
      )} */}
      {/* Update modal */}
      <MuiModal
        open={open}
        onClose={handleClose}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        textAlign="center"
      >
        <H1 fontSize={4} mb={4}>
          {locales.update}
        </H1>
        <Div>
          {locales.updateDescription}
          {locales.updateDescription_2}
        </Div>
        {/* Button to confirm update */}

        <Div mt={5} gap={2} textAlign="center">
          <MuiButton
            mx={1}
            maxWidth={200}
            fullWidth
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
            label={locales.continue}
            color="black"
            icon="feather icon-refresh-cw"
          />
          <MuiButton
            mx={1}
            maxWidth={200}
            onClick={handleClose}
            label={locales.cancel}
            color="inherit"
          />
        </Div>
      </MuiModal>
    </>
  );
}

export default VersionUpdatePopup;
